import React, { useState } from 'react';
import { analyzeDocumentForTopics, generateModuleContentForTopic, GeneratedTopic } from '../../../lib/module-generator';
import { supabase } from '../../../lib/supabase';
import './module-doc-uploader.scss';

interface ModuleDocUploaderProps {
    institutionId: string;
    onComplete: () => void;
    onCancel: () => void;
}

export const ModuleDocUploader: React.FC<ModuleDocUploaderProps> = ({ institutionId, onComplete, onCancel }) => {
    const [docText, setDocText] = useState('');
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [progress, setProgress] = useState<{ step: string; percent: number }>({ step: 'idle', percent: 0 });
    const [, setTopics] = useState<GeneratedTopic[]>([]);
    const [, setGeneratedModules] = useState<any[]>([]);
    const [error, setError] = useState<string | null>(null);

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const text = event.target?.result as string;
            setDocText(text);
        };
        reader.readAsText(file);
    };

    const handleAnalyze = async () => {
        if (!docText.trim()) {
            setError('Lütfen döküman içeriğini girin.');
            return;
        }

        setIsAnalyzing(true);
        setError(null);
        setProgress({ step: 'Döküman analiz ediliyor...', percent: 10 });

        try {
            // 1. Analyze Document
            const topicsResult = await analyzeDocumentForTopics(docText);
            setTopics(topicsResult);
            setProgress({ step: `${topicsResult.length} konu başlığı bulundu. Modüller oluşturuluyor...`, percent: 30 });

            // 2. Generate Modules for each topic
            const modulesData = [];
            const total = topicsResult.length;

            for (let i = 0; i < total; i++) {
                const topic = topicsResult[i];
                setProgress({
                    step: `Modül oluşturuluyor (${i + 1}/${total}): ${topic.title}`,
                    percent: 30 + Math.floor(((i + 1) / total) * 60)
                });

                const content = await generateModuleContentForTopic(topic, docText);

                // Format for DB
                const modulePayload = {
                    institution_id: institutionId,
                    title: topic.title,
                    description: topic.description,
                    is_active: true,
                    items: [
                        ...content.questions.map(q => ({
                            type: 'quiz',
                            question: q.question,
                            options: q.options,
                            correctIndex: q.correctIndex
                        })),
                        ...content.miniSims.map(m => ({
                            type: 'mini_sim',
                            aiQuestion: m.aiQuestion,
                            expectedAnswer: m.expectedAnswer,
                            avatarUrl: m.avatarUrl || '/avatars/julia.glb', // Default
                            voice: m.voice || 'Puck'
                        })),
                        {
                            type: 'main_sim',
                            missions: content.mainSim.missions,
                            systemInstruction: content.mainSim.systemInstruction,
                            userInstruction: content.mainSim.userInstruction,
                            avatarUrl: content.mainSim.avatarUrl || '/avatars/julia.glb',
                            voice: content.mainSim.voice || 'Puck'
                        }
                    ]
                };

                modulesData.push(modulePayload);
            }

            setGeneratedModules(modulesData);
            setProgress({ step: 'Veritabanına kaydediliyor...', percent: 95 });

            // 3. Save to DB
            if (!supabase) throw new Error('Supabase client not initialized');

            for (const mod of modulesData) {
                const { error: insertError } = await supabase.from('modules').insert(mod);
                if (insertError) throw insertError;
            }

            setProgress({ step: 'Tamamlandı!', percent: 100 });
            setTimeout(onComplete, 1000);


        } catch (err: any) {
            console.error(err);
            setError(err.message || 'Bir hata oluştu.');
            setIsAnalyzing(false);
        }
    };

    // Need to verify table name. 'simulation_modules' or 'modules'?
    // I will check supabase.ts or codebase search.
    // Assuming 'modules' based on previous context.
    // If table name is wrong, it will fail.
    // Let's use 'modules' for now.

    // saveToDB is handled inline during generation

    return (
        <div className="module-doc-uploader">
            <div className="uploader-header">
                <h2>Dökümandan Modül Oluşturucu</h2>
                <button className="btn-close" onClick={onCancel}>
                    <span className="material-symbols-outlined">close</span>
                </button>
            </div>

            {isAnalyzing ? (
                <div className="analyzing-state">
                    <div className="progress-bar">
                        <div className="fill" style={{ width: `${progress.percent}%` }}></div>
                    </div>
                    <p>{progress.step}</p>
                    <div className="spinner"></div>
                </div>
            ) : (
                <div className="input-area">
                    <p>Eğitim dökümanını metin olarak yapıştırın veya .txt dosyası yükleyin.</p>

                    <div className="file-input-wrapper">
                        <input type="file" accept=".txt,.md" onChange={handleFileUpload} />
                        <span className="info">Sadece metin dosyaları (.txt)</span>
                    </div>

                    <textarea
                        value={docText}
                        onChange={(e) => setDocText(e.target.value)}
                        placeholder="Döküman içeriğini buraya yapıştırın..."
                        rows={15}
                    />

                    {error && <div className="error-message">{error}</div>}

                    <div className="actions">
                        <button className="btn-secondary" onClick={onCancel}>İptal</button>
                        <button className="btn-primary" onClick={handleAnalyze} disabled={!docText.trim()}>
                            <span className="material-symbols-outlined">auto_awesome</span>
                            Analiz Et ve Oluştur
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
